import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portal, type PortalData, type PortalProposal } from '../lib/api';
import { ExternalLink, Download, Shield, FileText, Check, X, Phone, Mail, MessageCircle, Smartphone, Monitor, Search, Accessibility, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { FaInstagram, FaLinkedinIn, FaFacebookF } from 'react-icons/fa6';
import { format, type Locale } from 'date-fns';
import { es, enUS, pl, it } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import LanguageSelector from '../components/LanguageSelector';

interface RawDataCheck {
  id: string;
  title: string;
  description: string;
  status: 'fail' | 'warn' | 'pass';
  severity: 'critical' | 'warning' | 'ok';
}

interface RawData {
  checks?: RawDataCheck[];
  aiMessages?: {
    reportSummary?: string;
    portalCtaQuestion?: string;
    portalWhatsappMessage?: string;
    portalEmailSubject?: string;
    portalEmailBody?: string;
  };
  perfMetrics?: Record<string, unknown>;
  securityHeaders?: Record<string, unknown>;
  gbpVerified?: boolean;
  gbpDetails?: Record<string, unknown>;
}

function CheckItem({ check }: { check: RawDataCheck }) {
  const icon = check.status === 'fail'
    ? <XCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
    : check.status === 'warn'
      ? <AlertTriangle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
      : <CheckCircle size={16} className="text-green-400 shrink-0 mt-0.5" />;

  return (
    <div className="flex items-start gap-2 py-1">
      {icon}
      <div>
        <p className="text-white text-sm font-medium leading-tight">{check.title}</p>
        <p className="text-teal-200/60 text-xs mt-0.5">{check.description}</p>
      </div>
    </div>
  );
}

function ExpandableSection({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-6 py-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide w-full">
        <span className={color}>{title}</span>
        {open ? <ChevronUp size={14} className={color} /> : <ChevronDown size={14} className={color} />}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

const DATE_LOCALES: Record<string, Locale> = { es, en: enUS, pl, it };

export default function PortalPage() {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const dateFnsLocale = DATE_LOCALES[i18n.language] ?? es;

  useEffect(() => {
    if (!token) return;
    portal.get(token)
      .then(res => {
        setData(res);
        // Track page view
        portal.trackEvent(token, 'page_view');
      })
      .catch(e => setError(e instanceof Error ? e.message : t('portal.tokenInvalid')))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-emerald-800 flex items-center justify-center">
        <div className="text-teal-200 animate-pulse text-lg">{t('portal.loading')}</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-emerald-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <Shield size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('portal.accessDenied')}</h1>
          <p className="text-sm text-gray-500">
            {error || t('portal.accessDeniedDesc')}
          </p>
        </div>
      </div>
    );
  }

  const { client, audits, proposals } = data;
  const completedAudits = audits.filter(a => a.status === 'completed' && a.overall_score != null);
  const latestScore = completedAudits.length > 0 ? completedAudits[0]!.overall_score : null;

  const chartData = completedAudits
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map(a => ({
      date: format(new Date(a.created_at), 'dd/MM'),
      score: a.overall_score,
    }));

  const scoreColor = (s: number | null) =>
    s == null ? 'text-gray-400' : s >= 90 ? 'text-emerald-400' : s >= 80 ? 'text-green-400' : s >= 70 ? 'text-lime-400' : s >= 60 ? 'text-yellow-400' : s >= 50 ? 'text-amber-400' : s >= 40 ? 'text-orange-400' : s >= 30 ? 'text-red-400' : 'text-red-500';

  const scoreGlow = (s: number | null) =>
    s == null ? '' : s >= 80 ? 'drop-shadow-[0_0_15px_rgba(52,211,153,0.4)]' : s >= 50 ? 'drop-shadow-[0_0_15px_rgba(250,204,21,0.3)]' : 'drop-shadow-[0_0_15px_rgba(248,113,113,0.4)]';

  const handleDownload = (auditId: number) => {
    if (token) portal.trackEvent(token, 'report_download', { audit_id: auditId });
  };

  const handleAcceptProposal = (proposalId: number) => {
    if (token) {
      portal.trackEvent(token, 'proposal_accept', { proposal_id: proposalId });
      // Refresh data to show updated status
      portal.get(token).then(setData);
    }
  };

  const handleRejectProposal = (proposalId: number) => {
    if (token) {
      portal.trackEvent(token, 'proposal_reject', { proposal_id: proposalId });
      portal.get(token).then(setData);
    }
  };

  const handleContactClick = (type: string) => {
    if (token) portal.trackEvent(token, type);
  };

  // Parse rawData from latest audit for personalized CTA
  const latestRawData: RawData | null = (() => {
    const latest = completedAudits[0];
    if (!latest?.raw_data) return null;
    try { return JSON.parse(latest.raw_data as string); } catch { return null; }
  })();

  const portalMessages = latestRawData?.aiMessages;
  const ctaQuestion = portalMessages?.portalCtaQuestion || t('portal.ctaFallback');
  const whatsappMsg = portalMessages?.portalWhatsappMessage || `Hola, soy de ${client.business_name}. Vi el informe y me gustaría mejorar mi web.`;
  const emailSubject = portalMessages?.portalEmailSubject || `Web audit — ${client.business_name}`;
  const emailBody = (portalMessages?.portalEmailBody || `Hello Kanarix,\n\nI reviewed the audit report for ${client.domain} and I'd like to discuss improvements.\n\nThanks.`).replace(/\\n/g, '\n');

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-emerald-800">
      {/* Glass sweep animation */}
      <style>{`
        @keyframes glass-sweep {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(300%) skewX(-15deg); }
        }
        .glass-card {
          position: relative;
          overflow: hidden;
        }
        .glass-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
          transform: translateX(-100%) skewX(-15deg);
          animation: glass-sweep 6s ease-in-out infinite;
          pointer-events: none;
        }
        .glass-card:nth-child(2n)::after { animation-delay: 1s; }
        .glass-card:nth-child(3n)::after { animation-delay: 2s; }
        .glass-card:nth-child(4n)::after { animation-delay: 3s; }
      `}</style>

      {/* Top bar */}
      <div className="bg-black/20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <a href="https://kanarix.es" target="_blank" rel="noopener noreferrer" className="shrink-0">
            <img src="https://kanarix.es/images/logo/logo-dark-email.png" alt="Kanarix" className="h-12 w-auto" />
          </a>
          <div className="flex items-center gap-3 min-w-0">
            {client.domain && (
              <a href={`https://${client.domain}`} target="_blank" rel="noopener noreferrer" className="text-teal-300 hover:text-teal-200 text-sm flex items-center gap-1 truncate min-w-0">
                <span className="truncate">{client.domain}</span> <ExternalLink size={12} className="shrink-0" />
              </a>
            )}
            <LanguageSelector />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Hero */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white">{client.business_name}</h1>
          <p className="text-teal-200/70 mt-2">{t('portal.auditReport')}</p>
        </div>

        {/* Score card */}
        {(() => {
          const latest = completedAudits[0];
          return (
            <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 p-8 shadow-lg shadow-black/10">
              {latestScore != null ? (
                <>
                  <div className="text-center mb-6">
                    <p className="text-teal-200/60 text-sm mb-2">{t('portal.overallScore')}</p>
                    <p className={`text-7xl font-bold ${scoreColor(latestScore)} ${scoreGlow(latestScore)}`}>
                      {latestScore}
                    </p>
                    <p className="text-teal-200/40 text-sm mt-1">{t('portal.outOf100')}</p>
                  </div>

                  {/* Sub-scores */}
                  {latest && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
                      <SubScore icon={<Smartphone size={16} />} label={t('portal.perfMobile')} score={latest.perf_score_mobile} />
                      <SubScore icon={<Monitor size={16} />} label={t('portal.perfDesktop')} score={latest.perf_score_desktop} />
                      <SubScore icon={<Search size={16} />} label={t('portal.seo')} score={latest.seo_score_mobile} />
                      <SubScore icon={<Accessibility size={16} />} label={t('portal.accessibility')} score={latest.accessibility_score_mobile} />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <p className="text-teal-200/60 text-sm mb-2">{t('portal.overallScore')}</p>
                  <p className="text-3xl text-teal-200/40">{t('portal.scorePending')}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Audit Details: AI Summary + Checklist */}
        {(() => {
          const latest = completedAudits[0];
          if (!latest?.raw_data) return null;
          let rawData: RawData | null;
          try { rawData = JSON.parse(latest.raw_data as string); } catch { return null; }
          if (!rawData) return null;

          const checks = rawData.checks ?? [];
          const failChecks = checks.filter(c => c.status === 'fail');
          const warnChecks = checks.filter(c => c.status === 'warn');
          const passChecks = checks.filter(c => c.status === 'pass');

          return (
            <>
              {/* AI Summary */}
              {rawData.aiMessages?.reportSummary && (
                <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 p-6 shadow-lg shadow-black/10">
                  <h2 className="text-white font-semibold mb-3">{t('portal.analysisSummary')}</h2>
                  <div className="text-teal-200/70 text-sm leading-relaxed whitespace-pre-line">
                    {rawData.aiMessages.reportSummary}
                  </div>
                </div>
              )}

              {/* Checklist */}
              {checks.length > 0 && (
                <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 overflow-hidden shadow-lg shadow-black/10">
                  <div className="px-6 py-4 border-b border-white/10">
                    <h2 className="text-white font-semibold">{t('portal.analysisResults')}</h2>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="flex items-center gap-1 text-red-400"><XCircle size={12} /> {failChecks.length} {t('portal.errors')}</span>
                      <span className="flex items-center gap-1 text-yellow-400"><AlertTriangle size={12} /> {warnChecks.length} {t('portal.warnings')}</span>
                      <span className="flex items-center gap-1 text-green-400"><CheckCircle size={12} /> {passChecks.length} {t('portal.correct')}</span>
                    </div>
                  </div>

                  {/* Fails */}
                  {failChecks.length > 0 && (
                    <div className="px-6 py-3 border-b border-white/5">
                      <p className="text-xs font-medium text-red-400 uppercase tracking-wide mb-2">{t('portal.criticalErrors')}</p>
                      <div className="space-y-2">
                        {failChecks.map(c => (
                          <CheckItem key={c.id} check={c} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {warnChecks.length > 0 && (
                    <div className="px-6 py-3 border-b border-white/5">
                      <p className="text-xs font-medium text-yellow-400 uppercase tracking-wide mb-2">{t('portal.warningsLabel')}</p>
                      <div className="space-y-2">
                        {warnChecks.map(c => (
                          <CheckItem key={c.id} check={c} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Passes */}
                  <ExpandableSection title={`${passChecks.length} ${t('portal.passedChecks')}`} color="text-green-400">
                    <div className="space-y-2">
                      {passChecks.map(c => (
                        <CheckItem key={c.id} check={c} />
                      ))}
                    </div>
                  </ExpandableSection>
                </div>
              )}
            </>
          );
        })()}

        {/* Score chart */}
        {chartData.length > 1 && (
          <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 p-4 sm:p-6 shadow-lg shadow-black/10">
            <h2 className="text-white font-semibold mb-4">{t('portal.scoreEvolution')}</h2>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.5)' }} stroke="rgba(255,255,255,0.2)" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.5)' }} stroke="rgba(255,255,255,0.2)" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', background: '#1a3a3a', color: '#fff', fontSize: '13px' }}
                />
                <Line type="monotone" dataKey="score" stroke="#5eead4" strokeWidth={2} dot={{ fill: '#5eead4', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Audits list */}
        <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 overflow-hidden shadow-lg shadow-black/10">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-white font-semibold">{t('portal.auditHistory')}</h2>
          </div>
          {audits.length === 0 ? (
            <div className="p-8 text-center text-teal-200/40 text-sm">
              {t('portal.noAuditsYet')}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {audits.map(a => (
                <div key={a.id} className="flex items-center gap-4 px-6 py-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm border border-white/20 ${
                    a.overall_score == null ? 'bg-white/10 text-white/30' :
                    a.overall_score >= 80 ? 'bg-green-500/20 text-green-400' :
                    a.overall_score >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {a.overall_score ?? '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{a.business_name}</p>
                    <p className="text-teal-200/40 text-xs">
                      {format(new Date(a.created_at), t('portal.dateFormat'), { locale: dateFnsLocale })}
                    </p>
                  </div>
                  {a.status === 'completed' && a.report_html_key && token && (
                    <a
                      href={portal.downloadUrl(token, a.id)}
                      onClick={() => handleDownload(a.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 rounded-lg text-xs font-medium transition-colors border border-teal-400/30"
                    >
                      <Download size={12} />
                      {t('portal.download')}
                    </a>
                  )}
                  {a.status !== 'completed' && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      a.status === 'running' ? 'bg-blue-500/20 text-blue-300' :
                      a.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-red-500/20 text-red-300'
                    }`}>
                      {a.status === 'running' ? t('portal.statusRunning') : a.status === 'pending' ? t('portal.statusPending') : t('portal.statusError')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Proposals */}
        {proposals && proposals.length > 0 && proposals.map(proposal => (
          <ProposalCard
            key={proposal.id}
            proposal={proposal}
            onAccept={() => handleAcceptProposal(proposal.id)}
            onReject={() => handleRejectProposal(proposal.id)}
            onView={() => { if (token) portal.trackEvent(token, 'proposal_view', { proposal_id: proposal.id }); }}
          />
        ))}

        {/* Contact CTA */}
        <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 p-8 text-center space-y-4 shadow-lg shadow-black/10">
          <h2 className="text-xl font-bold text-white">{ctaQuestion}</h2>
          <p className="text-teal-200/60 text-sm">{t('portal.ctaDescription')}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={`https://wa.me/34680967251?text=${encodeURIComponent(whatsappMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleContactClick('whatsapp_click')}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg text-sm font-medium transition-colors border border-green-400/30"
            >
              <MessageCircle size={16} /> {t('portal.whatsapp')}
            </a>
            <a
              href={`mailto:info@kanarix.es?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
              onClick={() => handleContactClick('email_click')}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 rounded-lg text-sm font-medium transition-colors border border-teal-400/30"
            >
              <Mail size={16} /> {t('portal.email')}
            </a>
            <a
              href="tel:+34680967251"
              onClick={() => handleContactClick('phone_click')}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-sm font-medium transition-colors border border-blue-400/30"
            >
              <Phone size={16} /> {t('portal.call')}
            </a>
          </div>
        </div>
      </div>

      {/* Full Footer */}
      <footer className="bg-black/30 border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-10 sm:px-6 lg:px-8">
          {/* Mobile: Brand + contact stacked, Desktop: 4-col grid */}
          <div className="grid gap-8 grid-cols-1 lg:grid-cols-4">
            {/* Brand + Contact */}
            <div className="lg:col-span-1">
              <a href="https://kanarix.es" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://kanarix.es/images/logo/LOGO%20KANARIX-white.png"
                  alt="KANARIX"
                  loading="lazy"
                  className="h-10 w-auto"
                />
              </a>
              <p className="mt-3 text-sm leading-relaxed text-teal-200/50">
                {t('portal.footer.description')}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <a href="tel:+34680967251" className="flex items-center gap-2 text-sm text-teal-200/60 hover:text-teal-300 transition-colors">
                  <Phone className="h-4 w-4 shrink-0 text-teal-400" />
                  +34 680 967 251
                </a>
                <a href="mailto:info@kanarix.es" className="flex items-center gap-2 text-sm text-teal-200/60 hover:text-teal-300 transition-colors">
                  <Mail className="h-4 w-4 shrink-0 text-teal-400" />
                  info@kanarix.es
                </a>
              </div>
            </div>

            {/* Servicios + Empresa: 2-col on mobile, each 1-col on desktop */}
            <div className="grid grid-cols-2 gap-8 lg:contents">
              {/* Servicios */}
              <div>
                <div className="text-sm font-semibold uppercase tracking-wider text-white/80">{t('portal.footer.services')}</div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {[
                    [t('portal.footer.webDesign'), '/servicios/web/'],
                    [t('portal.footer.onlineStores'), '/servicios/ecommerce/'],
                    [t('portal.footer.localSeo'), '/servicios/seo/'],
                    [t('portal.footer.digitalMarketing'), '/servicios/marketing/'],
                    [t('portal.footer.automation'), '/servicios/automation/'],
                    [t('portal.footer.consulting'), '/servicios/consulting/'],
                  ].map(([name, path]) => (
                    <li key={path}>
                      <a href={`https://kanarix.es${path}`} target="_blank" rel="noopener noreferrer" className="text-teal-200/50 hover:text-teal-300 transition-colors">
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Empresa */}
              <div>
                <div className="text-sm font-semibold uppercase tracking-wider text-white/80">{t('portal.footer.company')}</div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {[
                    [t('portal.footer.aboutUs'), '/nosotros/'],
                    [t('portal.footer.faq'), '/faq/'],
                    [t('portal.footer.pricing'), '/precios/'],
                    [t('portal.footer.contact'), '/contacto/'],
                    [t('portal.footer.blog'), '/blog/'],
                  ].map(([name, path]) => (
                    <li key={path}>
                      <a href={`https://kanarix.es${path}`} target="_blank" rel="noopener noreferrer" className="text-teal-200/50 hover:text-teal-300 transition-colors">
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Social — centered on mobile, normal on desktop */}
            <div className="text-center lg:text-left">
              <div className="text-sm font-semibold uppercase tracking-wider text-white/80">{t('portal.footer.followUs')}</div>
              <div className="mt-3 flex gap-3 justify-center lg:justify-start">
                {[
                  { Icon: FaInstagram, href: 'https://www.instagram.com/kanarixfuture/', label: 'Instagram' },
                  { Icon: FaFacebookF, href: 'https://www.facebook.com/kanarixagenciadigital', label: 'Facebook' },
                  { Icon: FaLinkedinIn, href: 'https://www.linkedin.com/company/kanarix/', label: 'LinkedIn' },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-teal-200/60 hover:bg-teal-500 hover:text-white transition-colors border border-white/15"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Legal + Copyright */}
          <div className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-teal-200/30">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="https://kanarix.es/privacidad/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-300 transition-colors">{t('portal.footer.privacy')}</a>
              <span className="text-white/10">·</span>
              <a href="https://kanarix.es/cookies/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-300 transition-colors">{t('portal.footer.cookies')}</a>
              <span className="text-white/10">·</span>
              <a href="https://kanarix.es/terminos/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-300 transition-colors">{t('portal.footer.terms')}</a>
            </div>
            <p className="mt-3">©{new Date().getFullYear()} {t('portal.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ProposalCard({ proposal, onAccept, onReject, onView }: {
  proposal: PortalProposal;
  onAccept: () => void;
  onReject: () => void;
  onView: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = DATE_LOCALES[i18n.language] ?? es;
  useEffect(() => { onView(); }, []);

  const items = proposal.items ?? [];
  const subtotal = items.reduce((sum, i) => sum + i.price, 0);
  const discountAmount = subtotal * (proposal.discount_percent / 100);
  const total = subtotal - discountAmount;
  const oneTimeItems = items.filter(i => !i.is_recurring);
  const recurringItems = items.filter(i => i.is_recurring);

  const statusLabels: Record<string, { label: string; color: string }> = {
    draft: { label: t('portal.proposal.statusDraft'), color: 'text-gray-300' },
    sent: { label: t('portal.proposal.statusSent'), color: 'text-yellow-300' },
    viewed: { label: t('portal.proposal.statusViewed'), color: 'text-blue-300' },
    accepted: { label: t('portal.proposal.statusAccepted'), color: 'text-green-400' },
    rejected: { label: t('portal.proposal.statusRejected'), color: 'text-red-400' },
  };
  const status = statusLabels[proposal.status] ?? { label: t('portal.proposal.statusSent'), color: 'text-yellow-300' };

  return (
    <div className="glass-card bg-white/[0.07] backdrop-blur-md rounded-2xl border border-white/15 overflow-hidden shadow-lg shadow-black/10">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-teal-300" />
          <h2 className="text-white font-semibold">{proposal.title}</h2>
        </div>
        <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
      </div>

      {/* Items */}
      <div className="divide-y divide-white/5">
        {items.map(item => (
          <div key={item.id} className="flex items-center justify-between px-6 py-3">
            <div>
              <p className="text-white text-sm font-medium">{item.service_name}</p>
              {item.description && <p className="text-teal-200/40 text-xs">{item.description}</p>}
              {item.tier && item.tier !== 'addon' && (
                <span className="text-xs text-teal-300/60 bg-teal-500/10 px-1.5 py-0.5 rounded mt-0.5 inline-block">{item.tier}</span>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-white font-semibold">{item.price.toLocaleString('es-ES')} €</p>
              <p className="text-teal-200/40 text-xs">{item.is_recurring ? t('portal.proposal.monthly') : t('portal.proposal.oneTime')}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="px-6 py-4 border-t border-white/10 space-y-2">
        {oneTimeItems.length > 0 && recurringItems.length > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-teal-200/50">{t('portal.proposal.oneTimeTotal')}</span>
            <span className="text-white">{oneTimeItems.reduce((s, i) => s + i.price, 0).toLocaleString('es-ES')} €</span>
          </div>
        )}
        {recurringItems.length > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-teal-200/50">{t('portal.proposal.monthlyTotal')}</span>
            <span className="text-white">{recurringItems.reduce((s, i) => s + i.price, 0).toLocaleString('es-ES')} €/mes</span>
          </div>
        )}
        {proposal.discount_percent > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-green-300">{t('portal.proposal.discount')} ({proposal.discount_percent}%)</span>
            <span className="text-green-300">-{discountAmount.toLocaleString('es-ES')} €</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-white/10">
          <span className="text-teal-200/70 font-medium">{t('portal.proposal.total')}</span>
          <span className="text-2xl font-bold text-teal-300">{total.toLocaleString('es-ES')} €</span>
        </div>
      </div>

      {/* Notes */}
      {proposal.notes && (
        <div className="px-6 py-3 border-t border-white/5">
          <p className="text-teal-200/40 text-xs">{proposal.notes}</p>
        </div>
      )}

      {/* Valid until */}
      {proposal.valid_until && (
        <div className="px-6 py-2 text-xs text-teal-200/30">
          {t('portal.proposal.validUntil')} {format(new Date(proposal.valid_until), t('portal.dateFormat'), { locale: dateFnsLocale })}
        </div>
      )}

      {/* Accept / Reject actions */}
      {(proposal.status === 'sent' || proposal.status === 'viewed') && (
        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onAccept}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-xl text-sm font-medium transition-colors"
          >
            <Check size={16} /> {t('portal.proposal.accept')}
          </button>
          <button
            onClick={onReject}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-300/60 rounded-xl text-sm transition-colors"
          >
            <X size={16} /> {t('portal.proposal.reject')}
          </button>
        </div>
      )}

      {proposal.status === 'accepted' && (
        <div className="px-6 py-4 border-t border-white/10 text-center">
          <p className="text-green-400 font-medium text-sm flex items-center justify-center gap-2">
            <Check size={16} /> {t('portal.proposal.accepted')}
          </p>
        </div>
      )}
    </div>
  );
}

function SubScore({ icon, label, score }: { icon: React.ReactNode; label: string; score: number | null }) {
  const color = score == null ? 'text-gray-400' : score >= 90 ? 'text-emerald-400' : score >= 80 ? 'text-green-400' : score >= 70 ? 'text-lime-400' : score >= 60 ? 'text-yellow-400' : score >= 50 ? 'text-amber-400' : score >= 40 ? 'text-orange-400' : score >= 30 ? 'text-red-400' : 'text-red-500';
  const glow = score == null ? '' : score >= 80 ? 'drop-shadow-[0_0_8px_rgba(52,211,153,0.3)]' : score >= 50 ? 'drop-shadow-[0_0_8px_rgba(250,204,21,0.25)]' : 'drop-shadow-[0_0_8px_rgba(248,113,113,0.3)]';
  return (
    <div className="text-center space-y-1">
      <div className="flex items-center justify-center text-teal-200/50">{icon}</div>
      <p className={`text-2xl font-bold ${color} ${glow}`}>{score ?? '—'}</p>
      <p className="text-teal-200/40 text-xs">{label}</p>
    </div>
  );
}
